'use client';

import React from 'react';
import { MOCK_EXPENSES, EXPENSE_CATEGORIES, formatUGX } from './expensesData';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';

interface Props {
  farmFilter: string;
}

const CHART_COLORS = ['#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#6b7280', '#14b8a6', '#22c55e', '#f97316'];

export default function ExpenseDashboard({ farmFilter }: Props) {
  const expenses = farmFilter ? MOCK_EXPENSES.filter(e => e.farm === farmFilter) : MOCK_EXPENSES;

  // By category
  const byCat: Record<string, number> = {};
  expenses.forEach(e => { byCat[e.category] = (byCat[e.category] ?? 0) + e.amount; });
  const catData = EXPENSE_CATEGORIES
    .map(c => ({ name: c.label, value: byCat[c.value] ?? 0, color: CHART_COLORS[EXPENSE_CATEGORIES.indexOf(c) % CHART_COLORS.length] }))
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  // By farm
  const byFarm: Record<string, number> = {};
  expenses.forEach(e => { byFarm[e.farm] = (byFarm[e.farm] ?? 0) + e.amount; });
  const farmData = Object.entries(byFarm).map(([name, value]) => ({ name: name.replace(' Farm', '').replace(' Demonstration', ' Demo'), value }));

  // By month (last 3 months from mock data)
  const byMonth: Record<string, number> = {};
  expenses.forEach(e => {
    const m = e.date.substring(0, 7);
    byMonth[m] = (byMonth[m] ?? 0) + e.amount;
  });
  const monthData = Object.entries(byMonth).sort().map(([month, value]) => ({ month, value }));

  const total = expenses.reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-6">
      {/* Category Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Expenses by Category</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={catData} layout="vertical" margin={{ left: 8, right: 16, top: 0, bottom: 0 }}>
              <XAxis type="number" tickFormatter={v => (v / 1000000).toFixed(1) + 'M'} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: number) => formatUGX(v)} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                {catData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Category Share</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={catData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name.split(' ')[0]} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={10}>
                {catData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
              </Pie>
              <Tooltip formatter={(v: number) => formatUGX(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* By Farm & Monthly */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-4">Expenses by Farm</h3>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={farmData} margin={{ left: 8, right: 16, top: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tickFormatter={v => (v / 1000000).toFixed(1) + 'M'} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v: number) => formatUGX(v)} />
              <Bar dataKey="value" fill="#f97316" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-800 mb-3">Category Breakdown</h3>
          <div className="space-y-2">
            {catData.slice(0, 6).map(cat => {
              const pct = total > 0 ? (cat.value / total) * 100 : 0;
              return (
                <div key={cat.name}>
                  <div className="flex justify-between text-xs mb-0.5">
                    <span className="text-gray-700 font-medium">{cat.name}</span>
                    <span className="text-gray-500">{formatUGX(cat.value)} ({pct.toFixed(1)}%)</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: cat.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
